/**
 * API 配置管理工具
 * 用于独立应用模式下管理和验证 API 地址
 */

const API_BASE_URL_KEY = 'API_BASE_URL'
const API_HISTORY_KEY = 'API_URL_HISTORY'
const MAX_HISTORY_SIZE = 5

/**
 * 获取当前 API 基础地址
 * @returns {string|null} API 基础地址,如果未配置则返回 null
 */
export function getApiBaseUrl() {
  // 应用模式: 从 localStorage 读取
  if (process.env.VUE_APP_MODE === 'app') {
    const savedUrl = localStorage.getItem(API_BASE_URL_KEY)
    if (savedUrl) {
      return savedUrl
    }
    // 如果没有保存的地址,返回空字符串而不是默认地址
    // 这将引导用户到登录页面填写服务器地址
    return ''
  }

  // 传统模式: 使用 window.siteUrl 或当前域名 todo test
  // return 'http://127.0.0.1:88'
  return window.siteUrl || window.location.origin
}

/**
 * 保存 API 基础地址
 * @param {string} url - API 基础地址
 */
export function saveApiBaseUrl(url) {
  if (!url) return

  // 标准化 URL (移除末尾的斜杠)
  const normalizedUrl = url.replace(/\/$/, '')

  // 保存到 localStorage
  localStorage.setItem(API_BASE_URL_KEY, normalizedUrl)

  // 添加到历史记录
  addToHistory(normalizedUrl)
}

/**
 * 清除保存的 API 地址
 */
export function clearApiBaseUrl() {
  localStorage.removeItem(API_BASE_URL_KEY)
}

/**
 * 获取 API 地址历史记录
 * @returns {Array<string>} 历史记录数组
 */
export function getApiUrlHistory() {
  const historyStr = localStorage.getItem(API_HISTORY_KEY)
  if (!historyStr) return []

  try {
    return JSON.parse(historyStr)
  } catch (e) {
    console.error('解析 API 历史记录失败:', e)
    return []
  }
}

/**
 * 添加 URL 到历史记录
 * @param {string} url - API 地址
 */
function addToHistory(url) {
  let history = getApiUrlHistory()

  // 移除重复项
  history = history.filter(item => item !== url)

  // 添加到开头
  history.unshift(url)

  // 限制历史记录数量
  if (history.length > MAX_HISTORY_SIZE) {
    history = history.slice(0, MAX_HISTORY_SIZE)
  }

  // 保存
  localStorage.setItem(API_HISTORY_KEY, JSON.stringify(history))
}

/**
 * 清除历史记录
 */
export function clearApiUrlHistory() {
  localStorage.removeItem(API_HISTORY_KEY)
}

/**
 * 验证 URL 格式
 * @param {string} url - 待验证的 URL
 * @returns {boolean} 是否有效
 */
export function isValidUrl(url) {
  if (!url) return false

  try {
    const urlObj = new URL(url)
    // 只允许 http 和 https 协议
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
  } catch (e) {
    return false
  }
}

/**
 * 测试 API 连接
 * @param {string} url - API 基础地址
 * @returns {Promise<{success: boolean, message: string}>} 测试结果
 */
export async function testApiConnection(url) {
  if (!isValidUrl(url)) {
    return {
      success: false,
      message: 'URL 格式无效'
    }
  }

  try {
    // 标准化 URL
    const normalizedUrl = url.replace(/\/$/, '')

    // 尝试访问健康检查端点或登录端点
    const testUrl = `${normalizedUrl}/apimt/token/`

    const response = await fetch(testUrl, {
      method: 'OPTIONS', // 使用 OPTIONS 方法测试连接
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // 检查响应状态
    if (response.ok || response.status === 405) {
      // 405 表示端点存在但不支持 OPTIONS 方法,这也是成功的
      return {
        success: true,
        message: '连接成功'
      }
    }

    return {
      success: false,
      message: `服务器响应异常 (${response.status})`
    }
  } catch (error) {
    console.error('API 连接测试失败:', error)
    return {
      success: false,
      message: `连接失败: ${error.message}`
    }
  }
}

/**
 * 获取当前运行模式
 * @returns {string} 'app' 或 'default'
 */
export function getAppMode() {
  return process.env.VUE_APP_MODE || 'default'
}

/**
 * 是否为应用模式
 * @returns {boolean}
 */
export function isAppMode() {
  return getAppMode() === 'app'
}
